/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Integrates with the vault backend for user management.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';

// Types
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  image?: string;
  emailVerified: boolean;
}

export interface AuthSession {
  token: string;
  expiresAt: string;
}

interface AuthState {
  user: AuthUser | null;
  session: AuthSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  getGoogleOAuthUrl: (callbackUrl: string) => Promise<string>;
  handleGoogleCallback: (code: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

// Storage keys
const STORAGE_KEY_SESSION = 'vault_session_token';
const STORAGE_KEY_USER = 'vault_user';

// API base URL (relative to current origin in browser)
const getApiBaseUrl = () => {
  // In development, use the Vite proxy or direct URL
  // In production, use relative path
  return '';
};

// Context
const AuthContext = createContext<AuthContextValue | null>(null);

// Provider
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = useMemo(() => !!user && !!session, [user, session]);

  // Load session from storage on mount
  useEffect(() => {
    const loadStoredSession = async () => {
      try {
        const storedToken = localStorage.getItem(STORAGE_KEY_SESSION);
        const storedUser = localStorage.getItem(STORAGE_KEY_USER);

        if (storedToken && storedUser) {
          // Validate the session with the backend
          const response = await fetch(`${getApiBaseUrl()}/api/vault/auth/session`, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            setSession({
              token: storedToken,
              expiresAt: data.session.expiresAt,
            });
          } else {
            // Session invalid, clear storage
            localStorage.removeItem(STORAGE_KEY_SESSION);
            localStorage.removeItem(STORAGE_KEY_USER);
          }
        }
      } catch (error) {
        console.error('Failed to load stored session:', error);
        // Clear potentially corrupted storage
        localStorage.removeItem(STORAGE_KEY_SESSION);
        localStorage.removeItem(STORAGE_KEY_USER);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredSession();
  }, []);

  // Save session to storage whenever it changes
  useEffect(() => {
    if (session?.token) {
      localStorage.setItem(STORAGE_KEY_SESSION, session.token);
    }
    if (user) {
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    }
  }, [session, user]);

  // Login with email/password
  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch(`${getApiBaseUrl()}/api/vault/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    setUser(data.user);
    setSession(data.session);
  }, []);

  // Signup with email/password
  const signup = useCallback(async (email: string, password: string, name: string) => {
    const response = await fetch(`${getApiBaseUrl()}/api/vault/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Signup failed');
    }

    setUser(data.user);
    setSession(data.session);
  }, []);

  // Logout
  const logout = useCallback(() => {
    setUser(null);
    setSession(null);
    localStorage.removeItem(STORAGE_KEY_SESSION);
    localStorage.removeItem(STORAGE_KEY_USER);
  }, []);

  // Get Google OAuth URL
  const getGoogleOAuthUrl = useCallback(async (callbackUrl: string) => {
    const response = await fetch(
      `${getApiBaseUrl()}/api/vault/auth/oauth/google?callbackURL=${encodeURIComponent(callbackUrl)}`
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get OAuth URL');
    }

    return data.authUrl;
  }, []);

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (code: string) => {
    const response = await fetch(`${getApiBaseUrl()}/api/vault/auth/oauth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'OAuth callback failed');
    }

    setUser(data.user);
    setSession(data.session);
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    if (!session?.token) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/vault/auth/session`, {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setSession((prev) => prev ? { ...prev, expiresAt: data.session.expiresAt } : null);
      } else {
        // Session expired or invalid
        logout();
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
    }
  }, [session?.token, logout]);

  const value = useMemo(
    () => ({
      user,
      session,
      isLoading,
      isAuthenticated,
      login,
      signup,
      logout,
      getGoogleOAuthUrl,
      handleGoogleCallback,
      refreshSession,
    }),
    [
      user,
      session,
      isLoading,
      isAuthenticated,
      login,
      signup,
      logout,
      getGoogleOAuthUrl,
      handleGoogleCallback,
      refreshSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Optional: Hook that returns null if not in provider (for optional auth checks)
export function useAuthOptional() {
  return useContext(AuthContext);
}
